import i18n from 'i18next';

import { ConflictFieldConfig } from '@/modules/projects/types/project-conflicts';
import { MembershipType } from '@/modules/team/types/membership-type';
import { ConflictEntityReference } from '@/modules/team/types/team-member-conflicts';
import { getMembershipTypeLabel } from '@/modules/team/utils/membership-type.utils';

/**
 * Field keys for team member info conflict resolution
 * Uses form field names as keys for consistency
 */
export type TeamMemberInfoFieldKey =
  | 'status'
  | 'department'
  | 'role'
  | 'seniority'
  | 'membershipType'
  | 'country'
  | 'city'
  | 'managerId'
  | 'isManager';

/**
 * Field configurations for team member info conflict resolution
 * Defines how each field should be displayed in the conflicts table
 *
 * @param isPlaceholder - Whether the team member is a placeholder (hides contact/location fields)
 *
 * Rules:
 * 1. Fields with alwaysExcluded=true are never shown
 * 2. Other fields are shown only if values differ between form and any snapshot
 * 3. snapshotKey maps form field names to snapshot field names when they differ
 */
export const teamMemberInfoConflictFields = (
  isPlaceholder: boolean
): ConflictFieldConfig<TeamMemberInfoFieldKey>[] => [
  {
    key: 'status',
    labelKey: 'conflictFields.info.status',
    alwaysExcluded: true,
  },
  {
    key: 'department',
    labelKey: 'conflictFields.info.department',
    getFormValueForComparison: (formValue: { id?: number; name?: string } | null) => {
      return formValue?.id?.toString() || null;
    },
    getSnapshotValueForComparison: (snapshotValue: ConflictEntityReference | null) => {
      return snapshotValue?.id?.toString() || null;
    },
    valueFormatter: (value: { id?: number; name?: string } | null) => {
      if (!value) return i18n.t('common:table.noValue');
      if (value.name) return value.name;
      return i18n.t('common:table.noValue');
    },
  },
  {
    key: 'role',
    labelKey: 'conflictFields.info.role',
    getFormValueForComparison: (formValue: { id?: number; name?: string } | null) => {
      return formValue?.id?.toString() || null;
    },
    getSnapshotValueForComparison: (snapshotValue: ConflictEntityReference | null) => {
      return snapshotValue?.id?.toString() || null;
    },
    valueFormatter: (value: { id?: number; name?: string } | null) => {
      if (!value) return i18n.t('common:table.noValue');
      if (value.name) return value.name;
      return i18n.t('common:table.noValue');
    },
  },
  {
    key: 'seniority',
    labelKey: 'conflictFields.info.seniority',
    getFormValueForComparison: (formValue: { id?: number; name?: string } | null) => {
      return formValue?.id?.toString() || null;
    },
    getSnapshotValueForComparison: (snapshotValue: ConflictEntityReference | null) => {
      return snapshotValue?.id?.toString() || null;
    },
    valueFormatter: (value: { id?: number; name?: string } | null) => {
      if (!value) return i18n.t('common:table.noValue');
      if (value.name) return value.name;
      return i18n.t('common:table.noValue');
    },
  },
  {
    key: 'membershipType',
    labelKey: 'conflictFields.info.membershipType',
    getFormValueForComparison: (formValue: MembershipType | null | undefined) => {
      return formValue ?? null;
    },
    getSnapshotValueForComparison: (snapshotValue: MembershipType | null) => {
      return snapshotValue ?? null;
    },
    valueFormatter: (value: MembershipType | null) => {
      if (!value) return i18n.t('common:table.noValue');
      return getMembershipTypeLabel(value);
    },
    resolvedValueToFormValue: (value: MembershipType | null) => {
      return value ?? null;
    },
  },
  {
    key: 'country',
    labelKey: 'conflictFields.info.country',
    valueFormatter: (value: string | null) => {
      return value || i18n.t('common:table.noValue');
    },
    shouldShow: () => !isPlaceholder,
  },
  {
    key: 'city',
    labelKey: 'conflictFields.info.city',
    valueFormatter: (value: string | null) => {
      return value || i18n.t('common:table.noValue');
    },
    shouldShow: () => !isPlaceholder,
  },
  {
    key: 'managerId',
    snapshotKey: 'manager',
    labelKey: 'conflictFields.info.manager',
    // currentFormValues passes { id, name } for this field; snapshot also has { id, name }
    // Compare by id string on both sides
    getFormValueForComparison: (formValue: { id?: number | string; name?: string } | null) => {
      return formValue?.id?.toString() || null;
    },
    getSnapshotValueForComparison: (snapshotValue: { id?: number; name?: string } | null) => {
      return snapshotValue?.id?.toString() || null;
    },
    valueFormatter: (value: { id?: number | string; name?: string } | null) => {
      if (!value || !value.name) return i18n.t('common:table.noValue');
      return value.name;
    },
    // Convert { id, name } back to the form's ID string when resolution is applied
    resolvedValueToFormValue: (value: { id?: number | string; name?: string } | null) => {
      return value?.id?.toString() || null;
    },
  },
  {
    key: 'isManager',
    labelKey: 'conflictFields.info.isManager',
    valueFormatter: (value: boolean | null | undefined) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      return value
        ? i18n.t('authenticated.team:valueDisplay.yes')
        : i18n.t('authenticated.team:valueDisplay.no');
    },
  },
];
